import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/constants";

/**
 * Polls a list of messages for a ticket every `interval` ms.
 * Tracks the last seen `created_at` to fetch only deltas.
 */
export const useMessagePolling = (ticketId, token, interval = 2000) => {
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const lastTs = useRef(null);

  useEffect(() => {
    if (!ticketId || !token) return;
    let cancelled = false;
    let timer;
    const fetchOnce = async () => {
      try {
        const params = lastTs.current ? { after: lastTs.current } : {};
        const res = await axios.get(`${API}/tickets/${ticketId}/messages`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });
        if (cancelled) return;
        if (res.data?.length) {
          setMessages((prev) => {
            const seen = new Set(prev.map((m) => m.id));
            const next = [...prev];
            res.data.forEach((m) => {
              if (!seen.has(m.id)) next.push(m);
            });
            return next;
          });
          lastTs.current = res.data[res.data.length - 1].created_at;
        }
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load messages");
      }
    };
    fetchOnce();
    timer = setInterval(fetchOnce, interval);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [ticketId, token, interval]);

  const reset = () => {
    setMessages([]);
    lastTs.current = null;
  };

  return { messages, error, reset };
};

export const useTicketsPolling = (token, scope = "all", interval = 3000) => {
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const res = await axios.get(`${API}/tickets`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { scope },
        });
        if (cancelled) return;
        setTickets(res.data || []);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Failed to load tickets");
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, interval);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [token, scope, interval]);

  return { tickets, error };
};

